/**
 * 报销分摊的**端侧纯判定**（`DR-048` · 批次 83）
 *
 * 依据：`Q域PRD` `AC-Q3.6`（零额外输入 + 可微调）· `AC-Q6.4`（**禁端侧自算**）·
 * `DR-023` 反指标「端侧金额聚合 = 0」· `DR-036`（金额必带币种）
 *
 * ## 🔴 本文件一句算术都没有
 *
 * | 事情 | 谁做 |
 * |---|---|
 * | 比例分摊 / 尾差 / 跨年 / 超额 | **服务端**（内核 `nucleus/reimburse.ts`） |
 * | 池的合法性（≤ 垫付额 / 合计 === 报销额） | **服务端**（400 + `issues`） |
 * | 金额文本 → 整数分 | 共享内核 `yuanTextToCents()`（不做除法） |
 * | 金额展示 | 共享内核 `formatMoneyText()`（符号 / 币种 / 负号都在里面） |
 *
 * ⇒ 本文件只做三件事：**把表单打成请求** · **判断"哪些池被用户改过"** · **把服务端结论译成中文行**。
 *
 * ## 🔴 为什么"改过的池"要单独算
 *
 * `allocations` 是**可选键**（铁律 63）：不发 = 服务端自动分摊；发了空数组 = 语义含糊。
 * 所以只有用户**真的动过**某个池，才把它放进 `allocations`。
 * 判定按 **`poolKey` 在草稿里是否存在**，不按"值是否等于自动值" —— 后者要端侧知道自动值，
 * 那就等于把分摊结果抄了第二遍。
 */

import {
  CAT,
  centsToYuanText,
  formatMoneyText,
  yuanTextToCents,
  type Cents,
  type ReimburseAllocation,
  type ReimburseConfirmRequest,
  type ReimbursePoolResultView,
  type ReimbursePreviewRequest,
  type ReimbursePreviewView,
} from '@jiaying/shared';

/** 池键（与服务端同构：`categoryId#year`；`null` 分类用空串） */
export function poolKeyOf(categoryId: string | null, year: number): string {
  return `${categoryId ?? ''}#${String(year)}`;
}

/** 报销表单草稿（全文本 —— 与 `account-draft` / `contra-settle` 同一取向） */
export interface ReimburseDraft {
  /** 报销到账额（元，文本） */
  amountText: string;
  /** 到账日（`YYYY-MM-DD`） */
  dateText: string;
  /** 收款账户 id；`''` = 未选 */
  counterAccountId: string;
  /**
   * 微调：**池键 → 金额文本**。
   *
   * 🔴 只放**用户改过**的池（`poolKeyOf()` 作键）。没改的池不出现在这里，
   * 也就不会进请求（服务端按自动分摊）。
   */
  overrides: Readonly<Record<string, string>>;
}

export function emptyReimburseDraft(today: string, counterAccountId = ''): ReimburseDraft {
  return { amountText: '', dateText: today, counterAccountId, overrides: {} };
}

/**
 * 文本金额 → 整数分；空 / 非法 ⇒ `null`。
 *
 * 🔴 `yuanTextToCents()` 对第 3 位小数是**舍入**（它接受 `'1.005'`），而表单要的是**拒绝**。
 * 与 `contra-settle.ts` / `account-draft.ts` 的同名助手同一模式（真正的换算口径仍在内核）。
 */
function parseAmount(text: string): number | null {
  const s = text.trim();
  if (!/^\d+(\.\d{1,2})?$/.test(s)) return null;
  try {
    return yuanTextToCents(s) as number;
  } catch {
    return null;
  }
}

export type ReimbursePreviewBuildResult =
  | { ok: true; request: ReimbursePreviewRequest }
  | { ok: false; errors: readonly string[] };

/** 草稿 → 预演请求（`POST .../reimburse/preview`） */
export function buildPreviewRequest(draft: ReimburseDraft): ReimbursePreviewBuildResult {
  const errors: string[] = [];
  const amount = parseAmount(draft.amountText);
  if (amount === null || amount <= 0) errors.push('报销金额须为大于 0 的数字，最多两位小数');
  if (!/^\d{4}-\d{2}-\d{2}$/.test(draft.dateText.trim())) errors.push('日期格式须为 YYYY-MM-DD');
  if (errors.length > 0 || amount === null) return { ok: false, errors };
  return { ok: true, request: { amount, date: draft.dateText.trim() } };
}

export type ReimburseConfirmBuildResult =
  | { ok: true; request: ReimburseConfirmRequest }
  | { ok: false; errors: readonly string[] };

/**
 * 草稿 → 落账请求。
 *
 * 🔴 `allocations` **只在用户改过池时才带**（铁律 63）。空 ⇒ 整个键不出现，
 * 服务端走"零额外输入"分支（`AC-Q3.6` 的前半句）。
 */
export function buildConfirmRequest(draft: ReimburseDraft): ReimburseConfirmBuildResult {
  const base = buildPreviewRequest(draft);
  const errors: string[] = base.ok ? [] : [...base.errors];
  if (draft.counterAccountId.trim() === '') errors.push('请选择收款账户');

  const allocations: ReimburseAllocation[] = [];
  for (const [key, text] of Object.entries(draft.overrides)) {
    const idx = key.lastIndexOf('#');
    if (idx < 0) {
      errors.push(`内部分摊键格式错误：${key}`);
      continue;
    }
    const rawCategory = key.slice(0, idx);
    const year = Number(key.slice(idx + 1));
    if (!Number.isInteger(year)) {
      errors.push(`内部分摊键的年份不合法：${key}`);
      continue;
    }
    const cents = parseAmount(text);
    if (cents === null) {
      errors.push('微调金额须为不小于 0 的数字，最多两位小数');
      continue;
    }
    allocations.push({ categoryId: rawCategory === '' ? null : rawCategory, year, amountCents: cents });
  }

  if (errors.length > 0 || !base.ok) return { ok: false, errors };

  return {
    ok: true,
    request: {
      ...base.request,
      counterAccountId: draft.counterAccountId.trim(),
      ...(allocations.length > 0 ? { allocations } : {}),
    },
  };
}

/** 某一池的金额**默认显示值**（用户没改过时显示什么）—— 只读服务端下发的 `allocatedCents` */
export function poolAmountText(pool: ReimbursePoolResultView, draft: ReimburseDraft): string {
  const edited = draft.overrides[poolKeyOf(pool.categoryId, pool.year)];
  return edited ?? centsToYuanText(pool.allocatedCents);
}

/** 该池是不是**被用户改过**（只影响是否进 `allocations`，不影响任何金额） */
export function isPoolEdited(pool: ReimbursePoolResultView, draft: ReimburseDraft): boolean {
  return Object.prototype.hasOwnProperty.call(draft.overrides, poolKeyOf(pool.categoryId, pool.year));
}

/** 改写某一池的金额文本（返回**新**草稿，不改原对象） */
export function withPoolOverride(draft: ReimburseDraft, key: string, text: string): ReimburseDraft {
  return { ...draft, overrides: { ...draft.overrides, [key]: text } };
}

/** 撤销某一池的微调（回到服务端自动值） */
export function withoutPoolOverride(draft: ReimburseDraft, key: string): ReimburseDraft {
  const next: Record<string, string> = {};
  for (const [k, v] of Object.entries(draft.overrides)) if (k !== key) next[k] = v;
  return { ...draft, overrides: next };
}

/**
 * 池的展示标题（分类名由**调用方**从 `GET /api/categories` 取，本函数不查表）。
 *
 * `categoryId === null` 的池 = 未分类垫付，落账时冲减腿挂兜底 L2（`cat-other-offset`）。
 */
export function poolTitle(pool: ReimbursePoolResultView, categoryName: string | null): string {
  const name = pool.categoryId === null ? '未分类（落账时挂「报销/理赔冲减」）' : (categoryName ?? pool.categoryId);
  return `${name} · ${String(pool.year)} 年`;
}

/**
 * 预演摘要行（**全部金额来自服务端**，本函数只做"哪几行要显示"的判断与格式化）。
 *
 * 🔴 只在金额 > 0 时出行：0 元的「跨年转收入」出现在界面上会让用户以为发生了什么。
 */
export function previewSummaryLines(preview: ReimbursePreviewView): { label: string; value: string }[] {
  const cur = preview.currency;
  const lines = [
    { label: '该账户垫付合计', value: formatMoneyText(preview.advanceTotalCents as Cents, cur) },
    { label: '本次冲减原支出', value: formatMoneyText(preview.reductionCents as Cents, cur) },
  ];
  if (preview.crossYearIncomeCents > 0) {
    lines.push({
      label: '跨年部分（记收入，不追溯冲减）',
      value: formatMoneyText(preview.crossYearIncomeCents as Cents, cur),
    });
  }
  if (preview.excessCents > 0) {
    lines.push({
      label: '超出垫付（记收入）',
      value: formatMoneyText(preview.excessCents as Cents, cur),
    });
  }
  return lines;
}

/** 预演不可落账的原因（`issues` 原样转发 —— 端侧不翻译服务端口径，`AC-Q3.6`） */
export function blockingIssues(preview: ReimbursePreviewView): readonly string[] {
  if (preview.issues.length > 0) return preview.issues;
  if (!preview.balanced) return ['分摊结果不自洽（合计与报销额不相等），请调整金额或微调后重试。'];
  return [];
}

/** 收款账户的币种提示（`AC-Q4.5` 卡面已显示币种，表单再提示一次防"以为对方是美元"） */
export function reimburseAmountLabel(currency: string): string {
  return `报销到账额（${currency}）`;
}

/** 兜底冲减分类的 id（**唯一来源转发**，端侧不写字面量） */
export const REIMBURSE_OFFSET_CATEGORY_ID = CAT.OTHER_OFFSET;
